import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { audioPlayer } from '@/utils/audioPlayer';
import { NotificationItem } from '@/types/notification';
import { notificationApi } from '@/services/notificationApi';
import { auth } from '@/services/firebase';

const STREAM_API_URL = import.meta.env.VITE_STREAM_URL || 'https://api-offmufdgla-uc.a.run.app';

export function useRealtimeNotifications() {
  const { user, isAuthenticated } = useAuthStore();
  const [activeAlert, setActiveAlert] = useState<NotificationItem | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastProcessedIdRef = useRef<string | null>(null);

  const handleIncomingNotification = useCallback((notification: NotificationItem) => {
    if (!notification || !notification.id) return;
    if (lastProcessedIdRef.current === notification.id) return;
    lastProcessedIdRef.current = notification.id;

    console.log('🔔 [Realtime Notification Received]:', notification);

    // 1. Play sound chime based on notification type
    const notifType = notification.type || '';
    const entityType = notification.entityType || '';

    const isNewOrder =
      notifType === 'admin_order_created' ||
      notifType === 'order_created' ||
      notifType === 'new_order';

    const isLiveChat =
      entityType === 'chat' ||
      notifType.includes('live_chat') ||
      notifType.includes('chat');

    const isUserRegistration =
      notifType === 'admin_customer_registered' ||
      notifType === 'customer_registered';

    if (isNewOrder) {
      // 10.5 second store POS alarm ONLY on brand new orders!
      audioPlayer.playOrderChime();
    } else if (isLiveChat) {
      // Distinct double-ping chime for Live Support chat!
      audioPlayer.playLiveSupportChime();
    } else if (isUserRegistration) {
      audioPlayer.playUserRegistrationChime();
    } else {
      // Soft single chime for order status changes, courier updates, etc.
      audioPlayer.playGeneralNotificationChime();
    }

    // 2. Show floating banner
    setActiveAlert(notification);

    // 3. Notify app components (Navbar unread badge, Orders page)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('notifications:changed'));
      window.dispatchEvent(new CustomEvent('orders:new', { detail: notification }));
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user || !['admin', 'super_admin'].includes(user.role || '')) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    let retryTimeout: NodeJS.Timeout | null = null;
    let pollInterval: NodeJS.Timeout | null = null;
    let sseActive = true;

    const getFreshToken = async (forceRefresh = false): Promise<string | null> => {
      try {
        if (auth.currentUser) {
          const freshToken = await auth.currentUser.getIdToken(forceRefresh);
          localStorage.setItem('authToken', freshToken);
          return freshToken;
        }
      } catch (err) {
        console.warn('⚠️ [SSE] Could not get fresh token from auth:', err);
      }
      return localStorage.getItem('authToken');
    };

    const closeSSE = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };

    const connectSSE = async () => {
      if (!sseActive || document.visibilityState === 'hidden') return;
      try {
        const token = await getFreshToken();
        if (!token) return;

        closeSSE();

        const streamUrl = `${STREAM_API_URL}/admin/notifications/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(streamUrl);
        eventSourceRef.current = es;

        es.addEventListener('connected', () => {
          setIsConnected(true);
          console.log('⚡ [SSE] Connected to real-time notification stream');
        });

        es.addEventListener('notification', (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data) as NotificationItem;
            handleIncomingNotification(data);
          } catch (err) {
            console.error('❌ [SSE] Error parsing notification:', err);
          }
        });

        es.onerror = async (err) => {
          console.warn('⚠️ [SSE] Connection lost, refreshing token and retrying...', err);
          closeSSE();

          await getFreshToken(true);

          if (sseActive && document.visibilityState !== 'hidden') {
            retryTimeout = setTimeout(connectSSE, 10000);
          }
        };
      } catch (err) {
        console.warn('⚠️ [SSE] EventSource init failed:', err);
      }
    };

    // Backup polling (runs every 60s only when tab is active and SSE is disconnected)
    const startPolling = () => {
      let lastCount = -1;

      pollInterval = setInterval(async () => {
        if (document.visibilityState === 'hidden') return;
        try {
          const currentCount = await notificationApi.getUnreadCount('admin');
          if (lastCount !== -1 && currentCount > lastCount) {
            const latestList = await notificationApi.getNotifications({ scope: 'admin', limit: 1 });
            if (latestList && latestList[0]) {
              handleIncomingNotification(latestList[0]);
            }
          }
          lastCount = currentCount;
        } catch {
          // ignore
        }
      }, 60000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        connectSSE();
      } else {
        closeSSE();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    connectSSE();
    startPolling();

    return () => {
      sseActive = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (retryTimeout) clearTimeout(retryTimeout);
      if (pollInterval) clearInterval(pollInterval);
      closeSSE();
    };
  }, [isAuthenticated, user, handleIncomingNotification]);

  const dismissAlert = useCallback(() => {
    audioPlayer.stopOrderChime();
    setActiveAlert(null);
  }, []);

  return {
    activeAlert,
    dismissAlert,
    isConnected,
  };
}
